import { useRef, useState } from "react";
import { Camera, X } from "lucide-react";
import { fileToAvatarDataURI } from "@/lib/avatar";
import { toast } from "sonner";

/**
 * Reusable circular avatar uploader with center-cropped 256x256 JPEG preview.
 * value: string (URL or data URI) | null
 * onChange: (newDataUri | null) => void
 */
export default function AvatarUploader({ value, onChange, fallbackName = "U", size = 96, testid = "avatar-uploader" }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    try {
      const data = await fileToAvatarDataURI(file);
      onChange(data);
    } catch (err) {
      toast.error(err.message || "No se pudo procesar la imagen");
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const initials = (fallbackName || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        {value ? (
          <img src={value} alt="" className="w-full h-full rounded-full object-cover border-2 border-[#E1E5F0] bg-white" />
        ) : (
          <div className="w-full h-full rounded-full border-2 border-dashed border-[#E1E5F0] bg-[#F7F8FA] flex items-center justify-center">
            <span className="font-display font-extrabold text-[#6E7591] text-2xl">{initials}</span>
          </div>
        )}
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            data-testid={`${testid}-clear`}
            aria-label="Quitar foto"
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white border-2 border-[#E1E5F0] hover:border-[#D63384] flex items-center justify-center text-[#A52258]"
          ><X size={12} /></button>
        )}
      </div>

      <div className="flex flex-col gap-2 min-w-0">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={loading}
          data-testid={`${testid}-pick`}
          className="jasq-btn-ghost text-xs py-2 px-3 inline-flex items-center gap-2 w-fit"
        >
          <Camera size={14} /> {loading ? "Procesando…" : value ? "Cambiar foto" : "Subir foto"}
        </button>
        <p className="text-[11px] text-[#6E7591] leading-snug">JPG/PNG · se recorta cuadrada y se guarda a 256×256.</p>
        <input
          ref={inputRef}
          data-testid={`${testid}-input`}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handle}
        />
      </div>
    </div>
  );
}
